/**
 * Available options for all features in the ERC system.
 * This file defines all possible options that can be selected,
 * regardless of whether they have existing runs in the catalog.
 */

import type { FeatureKey } from './types'

/**
 * All available evaluation strategies (algorithms)
 * Based on core/algo/*.py implementations
 */
export const AVAILABLE_ALGORITHMS = [
  'GroundTruthAccuracy',
  'MartingaleStrategy',
  'WorldInTheLoop',
  'QualitativeJudge',
  'MutualPredictStrategy',
  'SycophanticReasoning',
  'GraderWrapper'
] as const

/**
 * All available domains
 * Based on core/domain/*.py implementations
 */
export const AVAILABLE_DOMAINS = [
  'Forecasting',
  'OpenReview',
  'CMVBinary',
  'CMVFreeForm',
  'Research',
  'Conceptual',
  'Overton',
  'WildChat',
  'ForumComment'
] as const

/**
 * All available reasoning modes
 * Based on core/reasoning/*.py implementations
 */
export const AVAILABLE_REASONING_MODES = [
  'DirectInference',
  'ChainOfThought',
  'SelfDebate',
  'BootstrapInterview'
] as const

/**
 * All available result analyzers
 * Based on core/analyzer/*.py implementations
 */
export const AVAILABLE_ANALYZERS = [
  'PerformanceComparisonAnalyzer',
  'EvaluationRelationshipAnalyzer',
  'CausalAttributionAnalyzer',
  'CrossSetupAgreementAnalyzer',
  'TokenLevelEvidenceAnalyzer',
  'ResultsCatalogAnalyzer',
  'TrajectoryScoreAnalyzer',
  'TrainingCausalEffectAnalyzer'
] as const

/**
 * Common model/policy names that are frequently used
 * Note: This is not exhaustive as new models can be added dynamically
 * Currently left empty - models will only appear if they exist in the catalog
 */
export const COMMON_MODELS = [
  // Empty for now - models will come from catalog only
] as const

/**
 * Common system prompts
 * Note: These are typically loaded from utils/policy_utils.py
 * Currently left empty - prompts will only appear if they exist in the catalog
 */
export const COMMON_SYSTEM_PROMPTS = [
  // Empty for now - prompts will come from catalog only
] as const

/**
 * Map of feature keys to their available options
 */
export const AVAILABLE_OPTIONS: Record<FeatureKey, readonly string[]> = {
  'Evaluation Strategy': AVAILABLE_ALGORITHMS,
  'Domain': AVAILABLE_DOMAINS,
  'Reasoning Mode': AVAILABLE_REASONING_MODES,
  'Model': COMMON_MODELS,
  'System Prompt': COMMON_SYSTEM_PROMPTS,
  'Result Analyzer': AVAILABLE_ANALYZERS
} as const

/**
 * Helper function to check if an option is available for a feature
 */
export function isOptionAvailable(feature: FeatureKey, option: string): boolean {
  const options = AVAILABLE_OPTIONS[feature]
  return options ? options.includes(option) : false
}

/**
 * Helper function to get all available options for a feature
 */
export function getAvailableOptions(feature: FeatureKey): readonly string[] {
  return AVAILABLE_OPTIONS[feature] || []
}